import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, message, Spin, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import apiService from '../../services/apiService';
import useLeadStore from '../../store/leadStore';

const { Option } = Select;
const { Text } = Typography;

const AssignLeadsModal = ({ visible, onClose, selectedLeadIds, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { bulkAssignLeads } = useLeadStore();

  // Fetch team members when modal becomes visible
  useEffect(() => {
    if (visible) {
      fetchTeamMembers();
    }
  }, [visible]);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/users/team-members');
      if (response.data.success) {
        setTeamMembers(response.data.data.teamMembers);
      }
    } catch (error) {
      console.error('Failed to load team members:', error);
      message.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      message.warning('Please select a team member to assign leads to');
      return;
    }

    setSubmitting(true);
    try {
      const result = await bulkAssignLeads(selectedLeadIds, selectedUserId);

      if (result.success) {
        message.success(`Successfully assigned ${result.data.assignedCount} leads`);
        onSuccess();
        onClose();
      } else {
        message.error(result.error || 'Failed to assign leads');
      }
    } catch (error) {
      console.error('Failed to assign leads:', error);
      message.error('Failed to assign leads');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Assign Leads"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          loading={submitting}
          onClick={handleAssign}
          disabled={!selectedUserId}
        >
          Assign
        </Button>
      ]}
    >
      <div className="mb-4">
        <Text>
          You are about to assign <strong>{selectedLeadIds?.length || 0}</strong> leads to a team member.
        </Text>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Team Member
        </label>
        {loading ? (
          <Spin size="small" />
        ) : (
          <Select
            placeholder="Select a team member"
            style={{ width: '100%' }}
            onChange={(value) => setSelectedUserId(value)}
            value={selectedUserId}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {teamMembers.map((member) => (
              <Option key={member.id} value={member.id}>
                <div className="flex items-center">
                  <UserOutlined className="mr-2" />
                  {member.name} {member.role === 'tenant_admin' ? '(Admin)' : ''}
                </div>
              </Option>
            ))}
          </Select>
        )}
      </div>
    </Modal>
  );
};

export default AssignLeadsModal;
